import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X, Cookie, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  consented: boolean;
  consentDate: string | null;
}

const COOKIE_CONSENT_KEY = "complyhub_cookie_consent";

const defaultPreferences: CookiePreferences = {
  essential: true, // Always required
  analytics: false,
  marketing: false,
  consented: false,
  consentDate: null,
};

export const getCookiePreferences = (): CookiePreferences => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === "object") {
        return {
          essential: true,
          analytics: parsed.analytics === true,
          marketing: parsed.marketing === true,
          consented: parsed.consented === true,
          consentDate: typeof parsed.consentDate === "string" ? parsed.consentDate : null,
        };
      }
    }
  } catch {
    // Ignore parse errors
  }
  return defaultPreferences;
};

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const stored = getCookiePreferences();
    setPreferences(stored);
    if (!stored.consented) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Storage events reach other tabs, not the tab that saved the choice.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) return;
      if (event.key !== COOKIE_CONSENT_KEY && event.key !== null) return;
      const latest = getCookiePreferences();
      setPreferences(latest);
      try {
        const w = window as unknown as {
          __chConsentUpdate?: (analytics: boolean, marketing: boolean) => void;
        };
        w.__chConsentUpdate?.(
          latest.consented === true && latest.analytics === true,
          latest.consented === true && latest.marketing === true,
        );
      } catch {
        // Keep preference state updated even if the Google callback fails.
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Listen for "Manage Cookies" event from footer
  useEffect(() => {
    const handleOpenPreferences = () => {
      setPreferences(getCookiePreferences());
      setIsVisible(true);
      setShowPreferences(true);
      setTimeout(() => setIsAnimating(true), 50);
    };

    window.addEventListener("open-cookie-preferences", handleOpenPreferences);
    return () => window.removeEventListener("open-cookie-preferences", handleOpenPreferences);
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    const updated = {
      ...prefs,
      consented: true,
      consentDate: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(updated));
    setPreferences(updated);
    // Update Google's consent signals; existing tags remain installed.
    try {
      const w = window as unknown as {
        __chConsentUpdate?: (analytics: boolean, marketing: boolean) => void;
      };
      w.__chConsentUpdate?.(updated.analytics, updated.marketing);
    } catch {
      // ignore
    }
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleAcceptAll = () => {
    savePreferences({
      ...preferences,
      analytics: true,
      marketing: true,
    });
  };

  const handleAcceptEssential = () => {
    savePreferences({
      ...preferences,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const togglePreference = (key: "analytics" | "marketing") => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-[60] transition-transform duration-300 ease-out",
        isAnimating ? "translate-y-0" : "translate-y-full"
      )}
    >
      <div className="bg-background border-t border-border shadow-lg">
        <div className="container py-4 md:py-5">
          <div className="flex flex-col gap-4">
            {/* Main banner content */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="h-5 w-5 text-cyan mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm text-foreground">
                    We use cookies to enhance your experience, analyse site usage, and support our marketing efforts.
                  </p>
                  <button
                    onClick={() => setShowPreferences(!showPreferences)}
                    className="text-sm text-cyan hover:underline inline-flex items-center gap-1"
                  >
                    Manage preferences
                    {showPreferences ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAcceptEssential}
                  className="text-sm min-h-[44px] min-w-[44px]"
                >
                  Essential Only
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="bg-cyan text-acai hover:bg-cyan/90 text-sm min-h-[44px] min-w-[44px]"
                >
                  Accept All
                </Button>
              </div>
            </div>

            {/* Preferences panel */}
            {showPreferences && (
              <div className="border-t border-border pt-4 space-y-4 animate-fade-in">
                <div className="grid gap-4 md:grid-cols-3">
                  {/* Essential cookies */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">Essential</span>
                      <Switch checked disabled className="data-[state=checked]:bg-cyan" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Required for core functionality including security, authentication, and accessibility.
                    </p>
                  </div>

                  {/* Analytics cookies */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">Analytics</span>
                      <Switch
                        checked={preferences.analytics}
                        onCheckedChange={() => togglePreference("analytics")}
                        className="data-[state=checked]:bg-cyan"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Help us understand how visitors interact with our site to improve the experience.
                    </p>
                  </div>

                  {/* Marketing cookies */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">Marketing</span>
                      <Switch
                        checked={preferences.marketing}
                        onCheckedChange={() => togglePreference("marketing")}
                        className="data-[state=checked]:bg-cyan"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Used to deliver relevant advertising and measure campaign effectiveness.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <a
                    href="/privacy-policy"
                    className="text-xs text-muted-foreground hover:text-cyan hover:underline"
                  >
                    Read our Privacy Policy
                  </a>
                  <Button
                    size="sm"
                    onClick={handleSavePreferences}
                    className="bg-cyan text-acai hover:bg-cyan/90"
                  >
                    Save Preferences
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
